import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Snackbar,
  Stack,
  TextField,
  Typography,
  alpha,
  keyframes
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Logo from '../Logo';
import { SkillCard } from '../../pages/Profile';
import { observer } from 'mobx-react-lite';
import { InterviewResult, Interview } from 'types/interview';
import { skillsStore } from 'stores/skillsStore';
import Download from '@mui/icons-material/Download';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import HighlightOff from '@mui/icons-material/HighlightOff';
import PlayArrow from '@mui/icons-material/PlayArrow';
import ExpandLess from '@mui/icons-material/ExpandLess';
import { useEffect, useState } from 'react';
import { personalDataStore } from 'stores/personalDataStore';
import LoadingScreen from 'components/LoadingScreen';
import { handleDownloadReport } from 'utils/downloadReport';
import { API_AUTH_URL, interviewApi } from 'api';
import { useNavigate } from 'react-router';
import { Icon } from '@iconify/react';

const discountPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 112, 67, 0.32), 0 4px 12px rgba(255, 112, 67, 0.18);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(255, 112, 67, 0), 0 6px 16px rgba(255, 112, 67, 0.22);
    transform: scale(1.02);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 112, 67, 0), 0 4px 12px rgba(255, 112, 67, 0.18);
    transform: scale(1);
  }
`;

const shimmerSlide = keyframes`
  0% { transform: translateX(-120%) skewX(-20deg); }
  70% { transform: translateX(220%) skewX(-20deg); }
  100% { transform: translateX(220%) skewX(-20deg); }
`;

const bigFiveTraits: { key: keyof InterviewResult['big_five']; label: string }[] = [
  { key: 'openness', label: 'Открытость опыту' },
  { key: 'conscientiousness', label: 'Сознательность' },
  { key: 'extraversion', label: 'Экстраверсия' },
  { key: 'agreeableness', label: 'Доброжелательность' },
  { key: 'neuroticism', label: 'Нейротизм' }
];

type DarkTriadKey = Exclude<keyof InterviewResult['dark_triade'], 'text'>;

const darkTriadTraits: { key: DarkTriadKey; label: string }[] = [
  { key: 'machiavellianism', label: 'Макиавеллизм' },
  { key: 'narcissism', label: 'Нарциссизм' },
  { key: 'psychopathy', label: 'Психопатия' }
];

const levelLabels: Record<string, string> = {
  JUNIOR: 'Начинающий',
  MIDDLE: 'Средний',
  SENIOR: 'Эксперт'
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

const HeaderRow = ({ label, value }: { label: string; value: string }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={{ xs: 0.5, sm: 2 }}
    alignItems={{ xs: 'flex-start', sm: 'baseline' }}
    sx={{ py: 1.25 }}
  >
    <Typography
      variant="caption"
      sx={{
        flexShrink: 0,
        width: { xs: 'auto', sm: 180 },
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        color: 'text.disabled'
      }}
    >
      {label}
    </Typography>
    <Typography variant="body1" sx={{ color: 'grey.900', fontWeight: 600, lineHeight: 1.4 }}>
      {value}
    </Typography>
  </Stack>
);

type Props = {
  interview: Interview | null;
  isCandidate?: boolean;
  isShared?: boolean;
};

const scoresGuard = (scores: InterviewResult | null | string | undefined) => {
  if (typeof scores === 'string') {
    return null;
  }

  return scores;
};

const sectionCardSx = {
  p: 3,
  borderRadius: 2,
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'grey.300'
};

export const Overline = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      fontWeight: 700,
      fontSize: 12,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      color: 'text.disabled',
      mb: 1
    }}
  >
    {children}
  </Typography>
);

type QuestionVideoCardProps = {
  interviewId: string;
  index: number;
  question: { text: string; title: string };
  isCandidate: boolean;
};

const QuestionVideoCard = ({
  interviewId,
  index,
  question,
  isCandidate
}: QuestionVideoCardProps) => {
  const [open, setOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  const handleToggle = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);

    if (videoUrl || loading) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      const url = `${API_AUTH_URL}media/${
        isCandidate ? 'user' : 'my'
      }/video/${interviewId}/${index}`.replace('//media', '/media');
      const response = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      if (!response.ok) {
        throw new Error('Не удалось загрузить видео');
      }

      const blob = await response.blob();
      setVideoUrl(URL.createObjectURL(blob));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить видео');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: 2.5,
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 2,
        bgcolor: 'background.paper'
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            {index + 1}. {question.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {question.text}
          </Typography>
        </Box>
        <Button
          onClick={handleToggle}
          variant="outlined"
          size="small"
          startIcon={open ? <ExpandLess /> : <PlayArrow />}
          sx={{
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 600,
            flexShrink: 0,
            alignSelf: { xs: 'stretch', sm: 'flex-start' }
          }}
        >
          {open ? 'Скрыть видео' : 'Показать видео'}
        </Button>
      </Stack>

      {open ? (
        <Box sx={{ mt: 2 }}>
          {loading ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={18} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Загрузка видео...
              </Typography>
            </Stack>
          ) : null}

          {error ? (
            <Typography variant="caption" sx={{ color: 'error.main' }}>
              {error}
            </Typography>
          ) : null}

          {videoUrl ? (
            <Box
              component="video"
              controls
              src={videoUrl}
              sx={{
                width: '100%',
                maxHeight: 480,
                borderRadius: 2,
                bgcolor: 'common.black',
                display: 'block'
              }}
            />
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

const InterviewResults = ({ interview, isCandidate = false, isShared = false }: Props) => {
  const { skillsWithGrade } = skillsStore;
  const { user } = personalDataStore;
  const navigate = useNavigate();
  const scores = scoresGuard(interview?.result);
  const [sharedLinkModal, setSharedlinkModal] = useState(false);
  const [sharedLink, setSharedLink] = useState<null | string>(null);
  const [sharedBtnIsClicked, setSharedBtnIsClicked] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    personalDataStore.getDiscounts();
  }, []);

  const handleClick = async () => {
    handleDownloadReport(interview?.report?.url as string);
  };

  const handleClickShareBtn = async () => {
    if (!interview) return;
    setSharedBtnIsClicked(true);
    try {
      if (sharedLink) {
        setSharedlinkModal(true);
        return;
      }

      const link = await interviewApi.createSharedLink(interview.id);
      const url = `${window.location.origin}/shared/results/${link.id}`;

      setSharedLink(url);

      setSharedlinkModal(true);
    } catch (e) {
      console.error(e);
    } finally {
      setSharedBtnIsClicked(false);
    }
  };

  const status = scores?.final_result?.status;
  const isMatch = status !== 'NOT_MATCH';

  if (!scores || !interview) {
    return <LoadingScreen label="Загружаем результат, процесс займет не больше 2 минут" />;
  }

  const fullName = [interview.user?.firstName, interview.user?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  const headerRows = [
    { label: 'Должность', value: interview?.position || '—' },
    { label: 'Сфера деятельности', value: interview?.speciality || '—' },
    {
      label: 'Уровень',
      value: levelLabels[user?.specialityLevel as keyof typeof levelLabels] || 'Средний'
    },
    { label: 'Дата окончания', value: interview.createdAt ? formatDate(interview.createdAt) : '-' },
    { label: 'Проходил', value: fullName || '—' }
  ];

  const handleCopyInviteLink = async () => {
    if (!sharedLink) return;
    try {
      await navigator.clipboard.writeText(sharedLink);
      setSnackbar({
        open: true,
        message: 'Ссылка успешно скопирована в буфер обмена',
        severity: 'success'
      });
    } catch {
      setSnackbar({
        open: true,
        message: 'Не удалось копировать ссылку для приглашения',
        severity: 'error'
      });
    }
  };

  return (
    <Stack
      spacing={4}
      sx={{
        height: isShared ? 'auto' : 'calc(var(--vh, 1vh) * 89)',
        pb: 6,
        overflow: !isShared ? 'auto' : 'unset',
      }}
    >
      {isShared ? (
        <Stack alignItems="center" sx={{ textAlign: 'center', overflow: 'hidden' }}>
          <Logo sx={{ width: 'auto', height: 134, mt: -5 }} />
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 520, mt: -6 }}>
            Система предназначена для проведения видео‑интервью и автоматической оценки кандидатов.
          </Typography>
        </Stack>
      ) : null}

      <Box
        sx={{
          mt: 2,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'grey.300'
        }}
      >
        {/* <Overline>Отчёт по интервью</Overline> */}

        <Stack sx={{ mb: 2 }} direction="row" spacing={4} alignItems="center" justifyContent="space-between">
          <Typography variant="h4" sx={{ color: 'grey.900', fontWeight: 700, mb: 3 }}>
            Результаты интервью
          </Typography>
          {interview.report?.url && !isCandidate ? (
            <Button
              onClick={handleClickShareBtn}
              variant="text"
              color="primary"
              disabled={sharedBtnIsClicked}
              startIcon={<Icon icon="mdi-share-all" />}
              sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 700 }}
            >
              Поделится результатом
            </Button>
          ) : null}
        </Stack>
        <Stack divider={<Divider flexItem />} sx={{ mb: 3 }}>
          {headerRows.map((row) => (
            <HeaderRow key={row.label} label={row.label} value={row.value} />
          ))}
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          {interview.report?.url ? (
            <Button
              onClick={handleClick}
              variant="contained"
              color="primary"
              startIcon={<Download />}
              sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 700 }}
            >
              Скачать отчёт
            </Button>
          ) : null}
          {personalDataStore.discount && !isCandidate ? (
            <Button
              variant="contained"
              onClick={() => navigate('/video-interview/check-devices')}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 14,
                px: 2.5,
                py: 1.1,
                color: 'common.white',
                background: 'linear-gradient(135deg, #FF8A00 0%, #FF3D00 60%, #D81B60 100%)',
                animation: `${discountPulse} 2.8s ease-in-out infinite`,
                '&:hover': {
                  background: 'linear-gradient(135deg, #FF8A00 0%, #FF8A65 60%, #E58BA5 100%)',
                  filter: 'brightness(1.03)'
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '40%',
                  height: '100%',
                  background:
                    'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.25) 50%, rgba(255,255,255,0) 100%)',
                  animation: `${shimmerSlide} 4.8s ease-in-out infinite`,
                  pointerEvents: 'none'
                }
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  position: 'relative',
                  zIndex: 1
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    px: 0.9,
                    py: 0.2,
                    borderRadius: 0.75,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    fontWeight: 800,
                    fontSize: 12,
                    letterSpacing: '0.5px'
                  }}
                >
                  -{personalDataStore.discount.amount}%
                </Box>
                Пройти еще раз
              </Box>
            </Button>
          ) : null}
        </Stack>
      </Box>

      <Dialog
        open={sharedLinkModal}
        onClose={() => setSharedlinkModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Ссылка для просмотра ваших результатов</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Скопируйте ссылку и отправьте её любому человеку с которым хотите поделиться
          </Typography>
          <TextField
            fullWidth
            value={sharedLink ?? ''}
            onFocus={(e) => e.target.select()}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleCopyInviteLink}
                    edge="end"
                    aria-label="Копировать ссылку"
                  >
                    <Icon icon="mdi:content-copy" width={20} height={20} />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSharedlinkModal(false)}
            color="inherit"
            sx={{ textTransform: 'none' }}
          >
            Закрыть
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleCopyInviteLink();
              setSharedlinkModal(false);
            }}
            startIcon={<Icon icon="mdi:content-copy" width={18} height={18} />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1, boxShadow: 'none' }}
          >
            Копировать и закрыть
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: 'grey.100',
          border: '1px solid',
          borderColor: 'grey.300'
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Общее заключение
        </Typography>
        <Typography variant="body1" sx={{ mb: interview.report?.url ? 3 : 0 }}>
          {scores?.summary}
        </Typography>
      </Box>

      {interview.questions?.length ? (
        <Box sx={sectionCardSx}>
          <Overline>Видеозаписи ответов</Overline>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 3 }}>
            Записи ответов кандидата на каждый вопрос интервью.
          </Typography>
          <Stack spacing={2}>
            {interview.questions.map((question, index) => (
              <QuestionVideoCard
                key={`${interview.id}-${index}`}
                interviewId={interview.id}
                index={index}
                question={question}
                isCandidate={isCandidate}
              />
            ))}
          </Stack>
        </Box>
      ) : null}

      <Box sx={sectionCardSx}>
        <Overline>Оценка навыков</Overline>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Уровни владения навыками
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 3 }}>
          Оценка с учётом ваших самооценок (отмечено на шкале)
        </Typography>
        <Stack divider={<Divider flexItem />} spacing={3}>
          {scores?.sections.map((i) => {
            const self = skillsWithGrade[i.title];
            const value = Number(i.score);
            return (
              <Box key={i.id}>
                <Box
                  sx={{
                    mb: 2,
                    '& .MuiCard-root': { border: 0 },
                    '& .MuiCardContent-root': { p: 1, pb: '0 !important' },
                    '& h3': {
                      whiteSpace: 'normal',
                      overflow: 'visible',
                      textOverflow: 'clip'
                    }
                  }}
                >
                  <SkillCard skill={i.title} grade={value} selfValue={self} />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                  {i.description}
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: 'secondary.main'
                    }}
                  >
                    Эмоции
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {i.emotions}
                  </Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: 'primary.main'
                    }}
                  >
                    Заключение
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {i.conclusion}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: 'primary.main'
                    }}
                  >
                    Рекомендации
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {i.recommendations}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {scores?.flesch_kincaid ? (
        <Box sx={sectionCardSx}>
          <Overline>Речь</Overline>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Сложность и ясность речи
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, color: 'text.primary' }}>
            {scores.flesch_kincaid.label}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
            {scores.flesch_kincaid?.description}
          </Typography>
          <Chip
            label={`Индекс Flesch–Kincaid: ${scores.flesch_kincaid.score}`}
            size="small"
            sx={{
              bgcolor: 'grey.200',
              color: 'text.primary',
              fontWeight: 600,
              borderRadius: 1
            }}
          />
        </Box>
      ) : null}

      {scores?.big_five ? (
        <Box sx={sectionCardSx}>
          <Overline>Личностный профиль</Overline>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Big Five
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 3 }}>
            Пятифакторная модель показывает выраженность ключевых личностных характеристик.
          </Typography>
          <Grid container spacing={2}>
            {bigFiveTraits.map(({ key, label }) => {
              const trait = scores.big_five[key];

              if (!trait) {
                return null;
              }

              return (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'grey.300',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5
                    }}
                  >
                    <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontWeight: 600 }}
                      >
                        {trait.score}
                        <Box component="span" sx={{ color: 'text.disabled' }}>
                          {' / 100'}
                        </Box>
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        height: 3,
                        borderRadius: 999,
                        bgcolor: 'grey.300',
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: `${Math.min(trait.score, 100)}%`,
                          height: '100%',
                          bgcolor: 'primary.main',
                          borderRadius: 999
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {trait.text}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ) : null}

      <Box sx={sectionCardSx}>
        <Overline>Рекомендации</Overline>
        <Typography variant="body1" sx={{ color: 'text.primary' }}>
          {scores?.recommendations}
        </Typography>
      </Box>

      {status !== null && (
        <Box
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: isMatch ? 'success.light' : 'error.light',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 3
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: 96,
              height: 96,
              borderRadius: '50%',
              bgcolor: (theme) =>
                alpha(isMatch ? theme.palette.success.main : theme.palette.error.main, 0.12),
              color: isMatch ? 'success.dark' : 'error.dark',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: { xs: 'center', sm: 'flex-start' }
            }}
          >
            <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1 }}>
              {scores.final_result.score}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.8 }}>
              из 10
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Overline>Общая оценка</Overline>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              {isMatch ? (
                <CheckCircleOutline sx={{ color: 'success.dark' }} fontSize="small" />
              ) : (
                <HighlightOff sx={{ color: 'error.dark' }} fontSize="small" />
              )}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: isMatch ? 'success.dark' : 'error.dark'
                }}
              >
                {isMatch ? 'Пройдено' : 'Не пройдено'}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {isMatch
                ? 'Вы обладаете необходимыми навыками для данной должности.'
                : 'Не расстраивайтесь — вы можете попробовать пройти интервью снова чуть позже.'}
            </Typography>
          </Box>
        </Box>
      )}

      {scores?.candidate_price && scores.final_result.score > 5 ? (
        <Box sx={sectionCardSx}>
          <Overline>Вилка</Overline>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Рекомендуемый уровень заработной платы
          </Typography>
          <Grid container spacing={2}>
            {[
              { label: 'Москва', value: scores.candidate_price.moscow },
              { label: 'Санкт‑Петербург', value: scores.candidate_price.saint_petersburg },
              { label: 'Регионы', value: scores.candidate_price.regions }
            ].map(({ label, value }) => (
              <Grid item xs={12} sm={4} key={label}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'grey.100',
                    border: '1px solid',
                    borderColor: 'grey.300'
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.disabled',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontWeight: 700,
                      display: 'block',
                      mb: 0.5
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : null}

      <Box sx={sectionCardSx}>
        <Overline>Дополнительные аспекты</Overline>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Тёмная триада
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
          Оценка выраженности тёмных черт личности на основе анализа интервью.
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.primary', mb: 3 }}>
          {scores.dark_triade.text}
        </Typography>
        <Grid container spacing={2}>
          {darkTriadTraits.map(({ key, label }) => {
            const trait = scores.dark_triade[key];

            if (!trait) {
              return null;
            }

            return (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5
                  }}
                >
                  <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {trait.score}
                      <Box component="span" sx={{ color: 'text.disabled' }}>
                        {' / 100'}
                      </Box>
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      height: 3,
                      borderRadius: 999,
                      bgcolor: 'grey.300',
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.min(trait.score, 100)}%`,
                        height: '100%',
                        bgcolor: 'primary.main',
                        borderRadius: 999
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {trait.text}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {isShared ? (
        <Box
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            bgcolor: 'grey.100',
            border: '1px solid',
            borderColor: 'grey.300',
            textAlign: 'center'
          }}
        >
          <Typography variant="body1" sx={{ color: 'text.primary', mb: 1.5 }}>
            Хотите сами пройти интервью или автоматизировать проведение интервью для соискателей?{' '}
            <br />
            <Link
              component={RouterLink}
              to="/login?login"
              sx={{ fontWeight: 700, textDecoration: 'none' }}
            >
              Войти в систему
            </Link>
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            По вопросам сотрудничества:{' '}
            <Link href="mailto:sales@yyny.ai" sx={{ fontWeight: 600, textDecoration: 'none' }}>
              sales@yyny.ai
            </Link>
          </Typography>
        </Box>
      ) : null}
    </Stack>
  );
};

export default observer(InterviewResults);
